import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "./initContext";
import { aggregatingFetch } from "../hooks/networkHook";
import { compareSimpleMap, dataHookFactory, initMapFactory, initMultiMapFactory } from "./utilities";
import { LazyValue, materializeValue } from "../utils/lazyValue";
import { useEffect, useMemo, useState } from "react";
import { useAsyncEffect } from "../hooks/asyncEffect";
import useWebsocket from "../hooks/websocketHook";
import { useDispatch } from "react-redux";
import { useNetworkState } from "../hooks/appStateHook";
import { ensureArray } from "../utils/arrayUtilities";
import { deepEquals } from "../utils/objectUtilities";

const entitySlice = createSlice({
    name: 'entity',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            for (const entity of ensureArray(action.payload)) {
                if (!state.data[entity.id]) {
                    state.data[entity.id] = entity;
                }
                const current = state.data[entity.id];
                const delta = {};
                for (const field of Object.keys(entity).filter(field => !['data','def'].includes(field))) {
                    if (entity[field] !== current[field]) {
                        delta[field] = entity[field];
                    }
                }
                if (!deepEquals(entity.data, current.data)) {
                    delta.data = entity.data;
                }
                if (!deepEquals(entity.def, current.def)) {
                    delta.def = entity.def;
                }
                if (Object.keys(delta).length > 0) {
                    state.data[entity.id] = { ...current, ...delta };
                }
            }
            ensureArray(action.payload).forEach(blob => state.data[blob.id] = blob);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(blob => delete state.data[blob.id]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => entitySlice.getInitialState());
    }
});

// # region network operations
const fetchEntity = aggregatingFetch(
    uuid => network => network.get('/api/v1/entity/' + encodeURIComponent(uuid), entitySlice.actions.set),
    uuids => network => network.post('/api/v1/entities', uuids, entitySlice.actions.set)
);
export const updateEntity = (network, delta) => network.put('/api/v1/entity', delta, entitySlice.actions.set);
// #endregion

// #region selectors
const selectEntityData = state => state.entity.data;
const selectEntity = uuid => createSelector([selectEntityData], entities => entities[uuid]);
const selectEntityMap = uuids => createSelector([selectEntityData], entities => uuids.reduce((obj, uuid) => { if (Object.hasOwn(entities, uuid)) { obj[uuid] = entities[uuid]; } return obj; }, {}));
// #endregion

// #region listeners
// NB there are no listeners because the listener is scoped to the entity
const entityListeners = {
}
// #endregion

const initMapByID = initMapFactory(fetchEntity, entityListeners);
export const [useEntity, realizeEntity] = dataHookFactory(selectEntity, initMapByID);
export const [useEntityMap, realizeEntityMap] = dataHookFactory(selectEntityMap, initMultiMapFactory(initMapByID, entityListeners), { comparator: compareSimpleMap });

// #region utilities
const CHRONOENTITY_URI_PREFIX = 'chronoentity:'

/**
 * A special LazyValue that resolves using a realizeEntity
 */
class ChronoEntity extends LazyValue {
    #uuid;
    constructor(realizationContext, uuid) {
        super(() => realizeEntity(realizationContext, uuid));
        this.#uuid = uuid;
    }
    toJSON() {
        return `${CHRONOENTITY_URI_PREFIX}${this.#uuid}`
    }
}
// #endregion

const chronoEntity = (realizationContext, uuid) => new ChronoEntity(realizationContext, uuid);

async function materializeChronoentityImpl(obj, materialize) {
    if (obj) {
        if (typeof obj === 'string' && obj.startsWith(CHRONOENTITY_URI_PREFIX)) {
            const uuid = obj.slice(CHRONOENTITY_URI_PREFIX.length);
            const value = await materialize(uuid);
            return { ...value, id: uuid };
        }
        if (obj && typeof obj === "object" && Object.keys(obj).length === 1 && obj.__chronoentity__init__) {
            obj = JSON.parse(Buffer.from(obj.__chronoentity__init__, "base64").toString("utf-8"));
        }
    }

    if (obj == null) {
        return obj;
    }

    if (Array.isArray(obj)) {
        let out = null;
        for (let i = 0; i < obj.length; i++) {
            const oldVal = obj[i];
            const newVal = await materializeChronoentityImpl(oldVal, materialize);

            if (newVal !== oldVal) {
                if (!out) {
                    out = [...obj];
                }
                out[i] = newVal;
            }
        }
        return out ?? obj;
    }

    if (typeof obj === 'object') {
        if (obj instanceof LazyValue) {
            return obj;
        }

        const fields = Object.getOwnPropertyNames(obj);
        let out = null;

        for (const field of fields) {
            const oldVal = obj[field];
            const newVal = await materializeChronoentityImpl(oldVal, materialize);

            if (newVal !== oldVal) {
                if (!out) {
                    out = { ...obj };
                }
                out[field] = newVal;
            }
        }

        return out ?? obj;
    }

    return obj;
}

export const serializePendingEntity = value => {
    return {
        __chronoentity__init__: Buffer.from(JSON.stringify(value), "utf-8").toString("base64")
    }
}

/**
 * This function will process an object and replace any chronoentity URIs with
 * the value of the blob as a value
 * @param {*} realizationContext standard context for running a realize
 * @param {*} obj the object to process
 * @returns a new object with chronoentity URIs resolved to values
 */
export async function materializeChronoentity(realizationContext, obj) {
    return materializeChronoentityImpl(obj, uuid => materializeValue(chronoEntity(realizationContext, uuid)));
}

export const useMaterializeChronoentity = (data) => {
    const dispatch = useDispatch();
    const { wsStatus } = useNetworkState();
    const [uuids, setUuids] = useState([]);
    const { register, emit } = useWebsocket();

    useEffect(() => {
        if (wsStatus === 'authenticated') {
            const cleanup = register('entityUpdated', entity => dispatch(entitySlice.actions.set(entity)));
            for (const uuid of uuids) {
                emit('connectEntity', { id: uuid });
            }
            return () => {
                cleanup();
                for (const uuid of uuids) {
                    emit('disconnectEntity', { id: uuid })
                }
            };
        }
    }, [register, uuids, wsStatus, emit, dispatch]);

    useAsyncEffect(async () => {
        const ids = []
        await materializeChronoentityImpl(data, uuid => {
            ids.push(uuid);
            return null;
        });
        return ids;
    },
    setUuids,
    [data]);

    const entityMap = useEntityMap(uuids);
    const [realData, setRealData] = useState(data);

    useAsyncEffect(async () => {
        let unresolved = false;
        const result = await materializeChronoentityImpl(data, uuid => {
            if (Object.hasOwn(entityMap, uuid)) {
                return entityMap[uuid];
            }
            unresolved = true;
            return `${CHRONOENTITY_URI_PREFIX}:${uuid}`;
        });
        return unresolved ? data : result;
    },
    setRealData,
    [data, entityMap]);

    return realData;
}

const reducerDetails = { name: entitySlice.name, reducer: entitySlice.reducer };
export default reducerDetails;